import {useState, type FormEvent} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Label} from '@/components/ui/label'
import {Tag} from '@/components/ui/tag'
import {Textarea} from '@/components/ui/textarea'
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {FieldError} from '@/components/field-error'
import {FormDialogFooter} from '@/components/form-dialog-footer'
import {PlanGate} from '@/components/plan-upsell'
import {Hint} from '@/components/ui/hint'
import {PorQue} from '@/components/ui/por-que'
import {useBateriaSolicitud, useSolicitarBateria} from '@/hooks/bateria/use-bateria-solicitudes'
import {useCapabilities} from '@/hooks/empresa/organizacion/use-businesses'
import {useNotaField} from '@/hooks/common/use-nota-field'
import {translateApiError} from '@/lib/errors'
import {formatDate} from '@/lib/risk'

/**
 * El operador solo ve el estado del pedido, nunca datos de la evaluación (Res. 2764/2022 art. 5).
 * El pedido no marca el estándar 4.1.2 (D6).
 */
export function BateriaSolicitudBlock({
  businessId,
  standardStatus,
  canEdit,
  reaplicacionVencida = false,
  puedeSolicitar = true,
}: {
  businessId: string
  standardStatus: string | null
  canEdit: boolean
  reaplicacionVencida?: boolean
  /** Falso con un informe externo vigente: el estado se sigue mostrando, solo se oculta el botón. */
  puedeSolicitar?: boolean
}) {
  const {t} = useTranslation()
  const {data: solicitud, isLoading} = useBateriaSolicitud(businessId, true)
  const {can_use_bateria} = useCapabilities(businessId)
  const solicitar = useSolicitarBateria(businessId)
  const [asking, setAsking] = useState(false)
  // Ref de callback: Radix monta el contenido después del primer efecto y `useRef` daría `null`; sin
  // contenedor el popover va a `document.body` y el primer Escape cierra el diálogo.
  const [nodoDialogo, setNodoDialogo] = useState<HTMLElement | null>(null)
  const notaField = useNotaField()
  const [error, setError] = useState<string | null>(null)

  if (isLoading) return null

  const pendiente = solicitud?.estado === 'pendiente'
  const enCurso = !!solicitud?.en_curso
  const descartada = solicitud?.estado === 'descartada'
  // Entregada cierra el ciclo igual que `descartada`: no tapa la solicitud siguiente.
  const entregada = solicitud?.estado === 'entregada'
  const canRequest =
    canEdit && puedeSolicitar && standardStatus === 'no_cumple' && (!solicitud || descartada || entregada)
  if (!pendiente && !enCurso && !entregada && !canRequest) return null

  function close() {
    notaField.reset()
    setError(null)
    solicitar.reset()
    setAsking(false)
  }

  // Con nombre propio: el check `doctrina_de_ui` busca `function validate*` en cada formulario.
  function validateForm(): boolean {
    return notaField.validate() !== null
  }

  function submit(e: FormEvent) {
    e.preventDefault()
    if (validateForm()) return
    solicitar.mutate(notaField.nota.trim() || null, {
      onSuccess: close,
      onError: (err) => setError(translateApiError(err)),
    })
  }

  return (
    <div className="mt-1 flex flex-wrap items-center gap-2" data-testid="bateria-solicitud-block">
      {pendiente && (
        <Tag tone="amber" data-testid="bateria-solicitud-chip">
          {t('bateriaSolicitudes.tenant.pendiente')}
        </Tag>
      )}
      {enCurso && (
        <Tag tone="green" data-testid="bateria-solicitud-chip">
          {t('bateriaSolicitudes.tenant.enCurso')}
        </Tag>
      )}
      {entregada && (
        <Tag tone="green" data-testid="bateria-solicitud-chip">
          {solicitud?.entregada_at
            ? t('bateriaSolicitudes.tenant.entregadaEl', {fecha: formatDate(solicitud.entregada_at)})
            : t('bateriaSolicitudes.tenant.entregada')}
        </Tag>
      )}
      {canRequest && (
        <>
          {descartada && (
            <span className="text-xs text-muted-foreground" data-testid="bateria-descartada-note">
              {t('bateriaSolicitudes.tenant.descartada')}
            </span>
          )}
          <PlanGate locked={!can_use_bateria} feature={t('bateriaSolicitudes.tenant.boton')} businessId={businessId}>
            <Button
              size="sm"
              variant={reaplicacionVencida ? 'default' : 'outline'}
              data-testid="solicitar-bateria"
              onClick={() => setAsking(true)}>
              {reaplicacionVencida
                ? t('bateriaSolicitudes.tenant.botonReaplicacion')
                : t('bateriaSolicitudes.tenant.boton')}
            </Button>
          </PlanGate>
          <span className="text-xs text-muted-foreground">
            {reaplicacionVencida
              ? t('bateriaSolicitudes.tenant.reaplicacionVencida')
              : t('bateriaSolicitudes.tenant.porQueCorto')}
          </span>
        </>
      )}

      <Dialog open={asking} onOpenChange={(open) => !open && close()}>
        <DialogContent ref={setNodoDialogo} data-testid="bateria-solicitud-dialog">
          <DialogHeader>
            <DialogTitle>{t('bateriaSolicitudes.tenant.dialogTitle')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4" noValidate>
            <p className="text-sm text-muted-foreground">{t('bateriaSolicitudes.tenant.dialogBody')}</p>
            {/* T8: custodia de datos de salud (Res. 2764/2022 art. 5), visible; la cita entera se pide en el `Hint`. */}
            <div className="flex flex-wrap items-baseline gap-1.5">
              <Hint>{t('bateriaSolicitudes.tenant.custodiaNote')}</Hint>
              <PorQue
                label={t('bateriaSolicitudes.tenant.custodiaPorqueLabel')}
                testId="bateria-solicitud-custodia-porque"
                contenidoTestId="bateria-solicitud-custodia-porque-contenido"
                contenedor={nodoDialogo}>
                {t('bateriaSolicitudes.tenant.custodiaPorque')}
              </PorQue>
            </div>
            <div className="space-y-2">
              <Label htmlFor="bateria-solicitud-nota">{t('bateriaSolicitudes.tenant.notaLabel')}</Label>
              <Textarea
                id="bateria-solicitud-nota"
                data-testid="bateria-solicitud-nota"
                value={notaField.nota}
                onChange={notaField.onChange}
                onBlur={notaField.onBlur}
                maxLength={notaField.maxLength}
                minRows={2}
                aria-invalid={!!notaField.notaError}
              />
              <FieldError error={notaField.notaError} />
            </div>
            <FormDialogFooter
              error={error}
              pending={solicitar.isPending}
              submitLabel={t('bateriaSolicitudes.tenant.submit')}
              onCancel={close}
            />
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
